import { z } from 'zod';

export enum ApiErrorCode {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  RATE_LIMITED = 'RATE_LIMITED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  // Reservation-specific domain error codes emitted by the reservation engine
  RESERVATION_CONFLICT = 'RESERVATION_CONFLICT',
  STALE_STATE = 'STALE_STATE',
  DISPLACEMENT_REQUIRED = 'DISPLACEMENT_REQUIRED',
  OPTION_EXTENSION_IMPOSSIBLE = 'OPTION_EXTENSION_IMPOSSIBLE',
  // Selected date range is shorter than the media type's configured minimum
  // reservation window (media_type_period_defaults.min_window_granularity)
  MIN_WINDOW_VIOLATION = 'MIN_WINDOW_VIOLATION',
  // Availability-windows domain error codes (availability-windows-model)
  RESERVATION_OVERLAPS_WINDOW = 'RESERVATION_OVERLAPS_WINDOW',
  AVAILABILITY_WINDOW_BLOCKED = 'AVAILABILITY_WINDOW_BLOCKED',
  OFFER_NOT_ACCEPTED = 'OFFER_NOT_ACCEPTED',
  OFFER_STATUS_UNCHANGED = 'OFFER_STATUS_UNCHANGED',
  OFFER_STATUS_NOT_USER_WRITABLE = 'OFFER_STATUS_NOT_USER_WRITABLE',
  CAMPAIGN_NUMBER_IMMUTABLE = 'CAMPAIGN_NUMBER_IMMUTABLE',
  // Campaign number override rejected: offer has no allocated campaign_number yet (offer-approval)
  CAMPAIGN_NUMBER_NOT_ALLOCATED = 'CAMPAIGN_NUMBER_NOT_ALLOCATED',
  // Campaign number override rejected: value collides with another offer's number (offer-approval)
  CAMPAIGN_NUMBER_TAKEN = 'CAMPAIGN_NUMBER_TAKEN',
  // Draft creation rejected: the generated campaign number collided with an
  // existing one more times than the bounded retry allows (offer-draft-numbering
  // / "Repeated collisions stop rather than exhausting the counter")
  CAMPAIGN_NUMBER_GENERATION_EXHAUSTED = 'CAMPAIGN_NUMBER_GENERATION_EXHAUSTED',
  SALESPERSON_ID_IMMUTABLE = 'SALESPERSON_ID_IMMUTABLE',
  SALESPERSON_INVALID = 'SALESPERSON_INVALID',
  // Waitlist queue error code
  NOT_QUEUEABLE = 'NOT_QUEUEABLE',
  // Public offer link error codes (public-offer-client-link)
  PUBLIC_LINK_EXPIRED = 'PUBLIC_LINK_EXPIRED',
  CLIENT_NAME_TAKEN = 'client_name_taken',
  // Client acronym conflict — mirrors CLIENT_NAME_TAKEN's 409 conflict shape
  // rather than a 400 validation error (client-entity / "Acronym duplicating
  // another client's is refused")
  CLIENT_ACRONYM_TAKEN = 'client_acronym_taken',
  // Supplier-specific domain error codes
  SUPPLIER_HAS_UNITS = 'supplier_has_units',
  SUPPLIER_NUMBER_COLLISION = 'supplier_number_collision',
  UNITS_HAVE_ACTIVE_RESERVATIONS = 'units_have_active_reservations',
  SUPPLIER_NAME_TAKEN = 'supplier_name_taken',
  // Availability-import confirm-time validation error codes
  PERIOD_LABEL_MAP_INCOMPLETE = 'PERIOD_LABEL_MAP_INCOMPLETE',
  GLOBAL_PERIOD_REQUIRED = 'GLOBAL_PERIOD_REQUIRED',
  MARK_REST_UNAVAILABLE_OUT_OF_SCOPE = 'MARK_REST_UNAVAILABLE_OUT_OF_SCOPE',
  DATE_RANGE_COLUMNS_REQUIRED = 'DATE_RANGE_COLUMNS_REQUIRED',
  MONTH_PERIOD_COLUMNS_INCOMPLETE = 'MONTH_PERIOD_COLUMNS_INCOMPLETE',
  SUPPLIER_NOT_RESOLVABLE = 'SUPPLIER_NOT_RESOLVABLE',
  // Permission-bundle enforcement (permission-matrix-admin-ui)
  PERMISSION_BUNDLE_INCOMPLETE = 'PERMISSION_BUNDLE_INCOMPLETE',
  // Offer version accept/reject superseded-version conflict (offer-versioning-diff)
  OFFER_VERSION_SUPERSEDED = 'OFFER_VERSION_SUPERSEDED',
  // Lock-reservations rejected because the target version is not ACCEPTED (offer-accept-reservation)
  OFFER_VERSION_NOT_ACCEPTED = 'OFFER_VERSION_NOT_ACCEPTED',
  // Release-rejected-units blocked because the reservation's status is excluded from release (client-offer-decision-persistence)
  RESERVATION_STATUS_BLOCKED = 'RESERVATION_STATUS_BLOCKED',
  // GET /api/import/template (ooh or dooh) has no active media_formats to build the
  // _ref_formats dropdown from -- a data-state precondition, not a 500 (dooh-import-template)
  IMPORT_TEMPLATE_NO_ACTIVE_FORMATS = 'IMPORT_TEMPLATE_NO_ACTIVE_FORMATS',
  // Media unit soft-delete blocked because it is still referenced by a live
  // DRAFT offer (media-unit-trash-recovery)
  MEDIA_UNIT_REFERENCED_BY_DRAFT_OFFER = 'MEDIA_UNIT_REFERENCED_BY_DRAFT_OFFER',
  // Offer cancellation rejected: offer is already CANCELLED (offer-lifecycle-reversibility)
  OFFER_ALREADY_CANCELLED = 'OFFER_ALREADY_CANCELLED',
  // Offer cancellation rejected: offer is trashed (offer-lifecycle-reversibility)
  OFFER_TRASHED = 'OFFER_TRASHED',
  // Client finalize (accept/reject, authenticated or anonymous) submitted a decision that
  // drops, un-accepts, or re-periods a locked carry-forward entry (offer-decision-carry-forward)
  LOCKED_DECISION_MODIFIED = 'LOCKED_DECISION_MODIFIED',
  // Order document generation refusal codes (offer-order-document-export, REQ-9.6).
  // UNPRICED_ACCEPTED_UNITS is deliberately not a member: an unpriced accepted
  // unit no longer refuses generation (bug fix, offer-order-document-export) —
  // see the X-Order-Document-Unpriced-Count response header instead.
  NO_ACCEPTED_VERSION = 'NO_ACCEPTED_VERSION',
  NO_ACCEPTED_UNITS = 'NO_ACCEPTED_UNITS',
  TOO_MANY_UNITS = 'TOO_MANY_UNITS',
  // A spreadsheet-import upload's file extension or actual content is not one of
  // the accepted spreadsheet formats (e.g. an ODF/.ods file, whether by extension
  // or by content when spoofed as .xlsx) — rejected before workbook parsing
  // (availability-import-pipeline, CU-86cb1npz2).
  UNSUPPORTED_FILE_FORMAT = 'UNSUPPORTED_FILE_FORMAT',
  // Availability-import confirm rejected because every data row failed to
  // resolve to a media unit — a more severe, whole-file signal distinct from
  // the per-encoding-shape rules above (availability-import-pipeline,
  // CU-86cbafj4j).
  ALL_ROWS_UNMATCHED = 'ALL_ROWS_UNMATCHED',
  // Campaign sale rejected: the offer is not a confirmed campaign, so its
  // inventory cannot be closed as sold yet (campaign-sale / "Campaign sale
  // endpoint").
  CAMPAIGN_NOT_CONFIRMED = 'CAMPAIGN_NOT_CONFIRMED',
}

export const ApiErrorResponseSchema = z.object({
  statusCode: z.number(),
  errorCode: z.nativeEnum(ApiErrorCode),
  message: z.string(),
  details: z.unknown().optional(),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
